import { AbTest, Lts, ScmpLiteCoreGeos } from "@product/rosetta-sdk";
import { useSyncedRef } from "@react-hookz/web";
import { useCallback, useEffect, useMemo } from "react";
import { graphql, readInlineData } from "react-relay";

import { config } from "shared/data";
import { paywall } from "shared/data/paywall";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { ExperimentAPINames, useActiveExperiment } from "scmp-app/lib/optimizely";
import { useUserGeolocation } from "scmp-app/lib/rosetta/hooks";
import type { hooksVelocityWallThresholdABTest$key } from "scmp-app/queries/__generated__/hooksVelocityWallThresholdABTest.graphql";

const FREE_TRIAL_PROMO_AB_TEST = "AppFreeTrialPromoSignInWall";

export const useSetStandardPaywallABTests = () => {
  const { experiment: scmpLiteCoreGeosExperiment } = useActiveExperiment(
    ScmpLiteCoreGeos.SCMP_LITE_CORE_GEOS_TEST_NAME,
  );
  const { experiment: ltsExperiment } = useActiveExperiment(Lts.LTS_AB_TEST_NAME);
  const { experiment: contextualPaywallMessagingExperiment } = useActiveExperiment(
    ExperimentAPINames.ContextualPaywallMessaging,
  );
  const { experiment: appFreeTrialPromoExperiment } = useActiveExperiment(FREE_TRIAL_PROMO_AB_TEST);

  useEffect(() => {
    [
      ltsExperiment,
      scmpLiteCoreGeosExperiment,
      contextualPaywallMessagingExperiment,
      appFreeTrialPromoExperiment,
    ].forEach(experiment => {
      if (!experiment) return;
      AbTest.setABTestVariablePianoCustomVariable({
        experimentId: experiment.id,
        testName: experiment.experimentName,
        variant: experiment.variation,
      });
    });
  }, [
    contextualPaywallMessagingExperiment,
    ltsExperiment,
    scmpLiteCoreGeosExperiment,
    appFreeTrialPromoExperiment,
  ]);
};

export const usePaywallMaskingABTest = () => {
  const { experiment } = useActiveExperiment(ExperimentAPINames.PaywallMasking);
  const isMobileOrTablet = useResponsive(theme.breakpoints.down("desktop"), false);
  const isHigherPaywallAtFirstParagraphABTest = useMemo(
    () => experiment?.variation?.name === "1",
    [experiment],
  );
  const isLowerPaywallABTest = useMemo(() => experiment?.variation?.name === "2", [experiment]);

  const getNthParagraphIndex = (position: number, schema: Schema) => {
    let count = 0;
    for (const [index, element] of schema.entries()) {
      if (count === position) {
        return index;
      }
      if (element.type === "p") {
        count++;
      }
    }
  };

  const getPaywallPosition = (schema: Schema) => {
    const isVeryShortArticle = schema.filter(node => node.type === "p").length < 10;

    if (isVeryShortArticle) return null;
    if (isHigherPaywallAtFirstParagraphABTest) return getNthParagraphIndex(1, schema);
    if (isLowerPaywallABTest)
      return isMobileOrTablet ? getNthParagraphIndex(7, schema) : getNthParagraphIndex(6, schema);
    return null;
  };

  return {
    getPaywallPosition,
  };
};

export const useVelocityWallThresholdABTest = (article_: hooksVelocityWallThresholdABTest$key) => {
  const article = readInlineData(
    graphql`
      fragment hooksVelocityWallThresholdABTest on Article @inline {
        paywallTypes {
          entityId
        }
      }
    `,
    article_,
  );

  const { experiment } = useActiveExperiment(ExperimentAPINames.VelocityWallThreshold);
  /**
   * Stores the user's initial LTS bucket score from local storage
   * because the value in localStorage may be updated later during piano execution.
   */
  const pianoAid = config.rosetta.piano.applicationId;
  const userInitialLtsBucket = useSyncedRef(Lts.getLtsBucketFromLocalStorage(pianoAid) ?? 0);

  const paywallTypesIds = useMemo(
    () => article.paywallTypes?.map(paywallType => paywallType?.entityId),
    [article.paywallTypes],
  );
  const paywallTypesHasNotVelocity = paywallTypesIds?.includes(paywall.notVelocity.entityId);

  const shouldApplyVelocityWallThresholdTest = useMemo(() => {
    const isUserInTargetingLtsBucket = userInitialLtsBucket.current === 0; // Bucket zone 0 and no_score users

    return (
      !!experiment?.variation?.name &&
      isUserInTargetingLtsBucket &&
      !paywallTypesHasNotVelocity &&
      paywallTypesIds?.includes(paywall.velocity.entityId)
    );
  }, [
    experiment?.variation?.name,
    paywallTypesHasNotVelocity,
    paywallTypesIds,
    userInitialLtsBucket,
  ]);

  const applyVelocityWallThresholdLogic = useCallback(() => {
    switch (experiment?.variation.name) {
      case "0":
        return paywallTypesIds?.includes(paywall.velocity.entityId);
      case "1":
        return paywallTypesIds?.includes(paywall.velocity2kViews.entityId);
      case "2":
        return paywallTypesIds?.includes(paywall.velocity3kViews.entityId);
      default:
        return false;
    }
  }, [experiment?.variation.name, paywallTypesIds]);

  return {
    applyVelocityWallThresholdLogic,
    shouldApplyVelocityWallThresholdTest,
  };
};

export const useLTSHardPaywallABTest = () => {
  const { experiment } = useActiveExperiment(ExperimentAPINames.LTSHardPaywall);
  const { checkTargetUserGeoLocation } = useUserGeolocation();
  const pianoAid = config.rosetta.piano.applicationId;
  const userInitialLtsBucket = useSyncedRef(Lts.getLtsBucketFromLocalStorage(pianoAid) ?? 0);
  const ltsBucket = userInitialLtsBucket.current;

  const isUserInABTestGeos = checkTargetUserGeoLocation(["HK", "SG"]);

  const ltsHardPaywallABTestTag = useMemo(() => {
    if (+ltsBucket < 7) return "";

    if (isUserInABTestGeos) {
      // HK and SG: Keep A/B test - return tag when user is in variant "1"
      const isLtsHardPaywallVariant = experiment?.variation?.name === "1";
      if (isLtsHardPaywallVariant) return "LTS-Hard-Paywall/hard-paywall-variant";
      return "";
    }

    // Non-HK/SG geos: Roll out high LTS wall (always apply variant)
    return "LTS-Hard-Paywall/hard-paywall-variant";
  }, [experiment, ltsBucket, isUserInABTestGeos]);

  return {
    ltsHardPaywallABTestTag,
  };
};
